import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'

interface Particle {
  id: number
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  rotationSpeed: number
  emoji: string
  scale: number
  birthTime: number
}

interface CelebrationEffectProps {
  onComplete?: () => void
  centerX?: number // タップ位置のX座標（ピクセル）
  centerY?: number // タップ位置のY座標（ピクセル）
}

export default function CelebrationEffect({ onComplete, centerX, centerY }: CelebrationEffectProps) {
  const [particles, setParticles] = useState<Particle[]>([])
  const onCompleteRef = useRef(onComplete)
  const centerXRef = useRef(centerX)
  const centerYRef = useRef(centerY)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    // パーティクルの絵文字（キラキラ系のみ）
    const emojis = ['✨', '⭐', '🌟', '💫', '✨', '⭐']

    // タップ位置を%に変換（デフォルトは画面中央）
    const startX = centerXRef.current ? (centerXRef.current / window.innerWidth) * 100 : 50
    const startY = centerYRef.current ? (centerYRef.current / window.innerHeight) * 100 : 50

    // 初期パーティクルを生成
    const initialParticles: Particle[] = []
    for (let i = 0; i < 50; i++) {
      const angle = (Math.PI * 2 * i) / 50 + (Math.random() - 0.5) * 0.5
      const speed = 0.8 + Math.random() * 0.8
      initialParticles.push({
        id: i,
        x: startX, // タップ位置から開始
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 30,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        scale: 0.5 + Math.random() * 0.5,
        birthTime: Date.now()
      })
    }

    setParticles(initialParticles)

    // アニメーションループ
    const interval = setInterval(() => {
      setParticles(prev => {
        return prev.map(p => ({
          ...p,
          x: p.x + p.vx,
          y: p.y + p.vy,
          rotation: p.rotation + p.rotationSpeed
        }))
      })
    }, 30)

    // 1800ms後にエフェクトを終了
    const timeout = setTimeout(() => {
      clearInterval(interval)
      onCompleteRef.current?.()
    }, 1800)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [])

  // 経過時間に基づいて透明度を計算（0.6から0に900msでフェード）
  const getOpacity = (particle: Particle) => {
    const elapsed = Date.now() - particle.birthTime
    const progress = Math.min(elapsed / 900, 1) // 0から1の範囲（900ms）
    return 0.6 * (1 - progress) // 0.6から0にフェード
  }

  return createPortal(
    <div className="fixed inset-0 pointer-events-none z-[9999]">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute text-xl sm:text-2xl"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            transform: `translate(-50%, -50%) rotate(${p.rotation}deg) scale(${p.scale})`,
            opacity: getOpacity(p)
          }}
        >
          {p.emoji}
        </div>
      ))}
    </div>,
    document.body
  )
}
